import { getAuth } from "firebase/auth";
import { isProduction } from "../firebase/config";

const PEAK_PLUS_PRICE_IDS = isProduction
  ? { monthly: "price_1UVJ5SS8gobt3IzcMFAsFTV7", yearly: "price_1UVJ5SS8gobt3IzcCCey6Bg0" }
  : { monthly: "price_1UThF2S39eCdVnEKss0xjUy3", yearly: "price_1UThF2S39eCdVnEKiILVWVN2" };

const PEAK_PREMIUM_PRICE_IDS = isProduction
  ? { monthly: "price_1UVJ5LS8gobt3Izc0lK1M2s4", yearly: "price_1UVJ5LS8gobt3IzcLD8VKMd6" }
  : { monthly: "price_1UThHyS39eCdVnEK2wqtNhlc", yearly: "price_1UThHyS39eCdVnEKML9NZi5J" };

const PEAK_PRICE_IDS = new Set([
  ...Object.values(PEAK_PLUS_PRICE_IDS),
  ...Object.values(PEAK_PREMIUM_PRICE_IDS),
  "price_1UThF2S39eCdVnEKss0xjUy3",
  "price_1UThF2S39eCdVnEKiILVWVN2",
  "price_1UThHyS39eCdVnEK2wqtNhlc",
  "price_1UThHyS39eCdVnEKML9NZi5J",
  "price_1UVJ5SS8gobt3IzcMFAsFTV7",
  "price_1UVJ5SS8gobt3IzcCCey6Bg0",
  "price_1UVJ5LS8gobt3Izc0lK1M2s4",
  "price_1UVJ5LS8gobt3IzcLD8VKMd6",
]);

const DISPLAY_PRICES = {
  plus: { month: 9.99, year: 99.99 },
  premium: { month: 17.99, year: 179.99 },
};

const CHECKOUT_URL = isProduction
  ? "https://createcheckoutsession-kp6dswkmca-uc.a.run.app"
  : "https://createcheckoutsession-kcepz7nnjq-uc.a.run.app";

let cachedIpCountry: string | null = null;

async function authHeader() {
  const currentUser = getAuth().currentUser;
  if (!currentUser) throw new Error("User not authenticated");
  const token = await currentUser.getIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

export const StripeService = {
  getPeakPriceId(plan: "plus" | "premium", billingPeriod: "month" | "year"): string {
    const ids = plan === "plus" ? PEAK_PLUS_PRICE_IDS : PEAK_PREMIUM_PRICE_IDS;
    return billingPeriod === "year" ? ids.yearly : ids.monthly;
  },

  getPeakDisplayPrice(plan: "plus" | "premium", billingPeriod: "month" | "year"): number {
    return DISPLAY_PRICES[plan][billingPeriod];
  },

  async detectCountryFromIp(): Promise<string | null> {
    if (cachedIpCountry) return cachedIpCountry;
    try {
      const response = await fetch("https://ipapi.co/json/", {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return null;
      const data = (await response.json()) as { country_name?: string };
      cachedIpCountry = data.country_name || null;
      return cachedIpCountry;
    } catch {
      return null;
    }
  },

  async validateCountryForPricing(profileCountry?: string | null): Promise<string> {
    if (!profileCountry) return "United States";
    const ipCountry = await this.detectCountryFromIp();
    if (!ipCountry) return profileCountry;
    return ipCountry.toLowerCase() === profileCountry.toLowerCase()
      ? profileCountry
      : "United States";
  },

  async createCheckoutSession(
    priceId: string,
    customerId: string,
    successUrl: string,
    cancelUrl: string,
    uid: string,
    name: string,
    trialDays?: number,
    switchFromSubscriptionId?: string,
    source?: "onboarding",
  ): Promise<string | null> {
    if (!PEAK_PRICE_IDS.has(priceId)) {
      console.error("Refusing checkout for a price outside the PEAK account", priceId);
      return null;
    }
    const headers = await authHeader();
    const response = await fetch(CHECKOUT_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({
        priceId,
        customerId,
        successUrl,
        cancelUrl,
        uid,
        name,
        code: "",
        trialDays: trialDays ?? 0,
        ...(switchFromSubscriptionId ? { switchFromSubscriptionId } : {}),
        ...(source ? { source } : {}),
      }),
    });
    if (!response.ok) throw new Error(`Checkout failed (${response.status})`);
    const data = (await response.json()) as { url?: string };
    return data.url ?? null;
  },
};
